/**
 * 🌍 TIMEZONE UTILITIES
 * Handle booking dates in service location timezones
 *
 * Problem:
 * - Bookings stored in UTC
 * - Users in different timezones see wrong dates
 * - Lagos booking on Dec 27 shows as Dec 26 for EST users
 *
 * Solution:
 * - Backend returns timezone metadata with bookings
 * - Frontend displays dates in service location timezone
 * - Show "Dec 27 (Lagos time)" regardless of user's timezone
 */

/**
 * Format date in service location timezone
 * @param dateString ISO date string from backend
 * @param timezone IANA timezone (e.g., 'Africa/Lagos', 'Africa/Nairobi')
 * @param options Intl.DateTimeFormat options
 * @returns Formatted date string in service timezone
 *
 * @example
 * const booking = { startDate: "2025-12-27T00:00:00.000Z", timezone: "Africa/Lagos" };
 * formatDateInServiceTimezone(booking.startDate, booking.timezone);
 * // Returns: "Dec 27, 2025" (in Lagos time, not user's local time)
 */
export const formatDateInServiceTimezone = (
  dateString: string,
  timezone?: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }
): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: timezone || 'Africa/Lagos', // Default to Lagos if no timezone provided
    });
    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting date in timezone:', error, { dateString, timezone });
    // Fallback to simple date formatting
    return new Date(dateString).toLocaleDateString();
  }
};

/**
 * Format date range in service location timezone
 * @param startDate ISO date string
 * @param endDate ISO date string
 * @param timezone IANA timezone
 * @returns Formatted date range (e.g., "Dec 27 - Jan 2")
 *
 * @example
 * formatDateRangeInServiceTimezone(
 *   "2025-12-27T00:00:00.000Z",
 *   "2026-01-02T00:00:00.000Z",
 *   "Africa/Lagos"
 * );
 * // Returns: "Dec 27 - Jan 2"
 */
export const formatDateRangeInServiceTimezone = (
  startDate: string,
  endDate: string,
  timezone?: string
): string => {
  const start = formatDateInServiceTimezone(startDate, timezone, { month: 'short', day: 'numeric' });
  const end = formatDateInServiceTimezone(endDate, timezone, { month: 'short', day: 'numeric' });
  return `${start} - ${end}`;
};

/**
 * Get timezone label for display
 * @param timezone IANA timezone (e.g., 'Africa/Lagos')
 * @param location Service location name (e.g., 'Lagos')
 * @returns Formatted timezone label (e.g., "(Lagos time)")
 *
 * @example
 * getTimezoneLabel("Africa/Lagos");
 * // Returns: "(Lagos time)"
 *
 * getTimezoneLabel(undefined, "Lagos");
 * // Returns: "(Lagos time)"
 */
export const getTimezoneLabel = (timezone?: string, location?: string): string => {
  if (!timezone && !location) return '';
  if (location && !timezone) return `(${location} time)`;

  // Extract city from timezone (e.g., 'Africa/Lagos' -> 'Lagos')
  const city = timezone?.split('/')[1]?.replace(/_/g, ' ') || location;
  return city ? `(${city} time)` : '';
};

/**
 * Format date with timezone label
 * @param dateString ISO date string
 * @param timezone IANA timezone
 * @param location Service location name
 * @returns Date with timezone label (e.g., "Dec 27, 2025 (Lagos time)")
 *
 * @example
 * formatDateWithTimezone(
 *   "2025-12-27T00:00:00.000Z",
 *   "Africa/Lagos",
 *   "Lagos"
 * );
 * // Returns: "Dec 27, 2025 (Lagos time)"
 */
export const formatDateWithTimezone = (
  dateString: string,
  timezone?: string,
  location?: string
): string => {
  const formattedDate = formatDateInServiceTimezone(dateString, timezone);
  const tzLabel = getTimezoneLabel(timezone, location);
  return tzLabel ? `${formattedDate} ${tzLabel}` : formattedDate;
};

/**
 * Format time in service location timezone
 * @param dateString ISO date string
 * @param timezone IANA timezone
 * @returns Formatted time (e.g., "2:00 PM")
 */
export const formatTimeInServiceTimezone = (
  dateString: string,
  timezone?: string
): string => {
  return formatDateInServiceTimezone(dateString, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format datetime in service location timezone
 * @param dateString ISO date string
 * @param timezone IANA timezone
 * @returns Formatted datetime (e.g., "Dec 27, 2025 at 2:00 PM")
 */
export const formatDateTimeInServiceTimezone = (
  dateString: string,
  timezone?: string
): string => {
  return formatDateInServiceTimezone(dateString, timezone, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};
